import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, useMotionValue, animate } from 'framer-motion';

/**
 * Trailer — Scroll-driven cinematic video block with full custom player.
 *
 *  ─── Player UI ─────────────────────────────────────────────────────
 *  Custom overlay (Play/Pause · seek bar · mute · time) rendered on top
 *  of the <video>. Native browser controls are disabled. The overlay
 *  follows the video into every mode (inline / fullscreen / mini), with
 *  auto-hide after 3s of mouse idle in fullscreen only.
 *
 *  ─── Modes ────────────────────────────────────────────────────────
 *    inline      Tracks an invisible placeholder inside the section.
 *    fullscreen  position:fixed inset:0. Triggered automatically the
 *                first time the section centre crosses the viewport
 *                centre — unless the user already started manual
 *                playback, in which case we still go fullscreen but
 *                don't re-call play().
 *    mini        Bottom-right floating 260×146 player. Reached by
 *                scrolling past the section, or pressing Escape.
 *    closed      User dismissed via the × button.
 *
 *  ─── Audio fade ───────────────────────────────────────────────────
 *  Fade hooks are wired to the <video>'s `play` and `pause` events,
 *  so any path (manual click, autoplay, Esc, scroll, close) produces
 *  a smooth fade-in / fade-out. Target volume depends on mode.
 *
 *  ─── Browser caveats ──────────────────────────────────────────────
 *  Native requestFullscreen() requires a click → CSS pseudo-fullscreen
 *  via position:fixed. Autoplay-with-sound needs prior user gesture →
 *  we start muted, try to unmute right after play(); if the browser
 *  refuses, a "♪ Tap for sound" overlay appears.
 */

const EASE = [0.16, 1, 0.3, 1];
const MINI_W = 260;
const MINI_H = 146; // 16:9
const MINI_PAD_RIGHT = 24;
const MINI_PAD_BOTTOM = 100; // clears the fixed audio Player (~80px) + margin

const targetVolForMode = (m) => (m === 'mini' ? 0.45 : 0.85);

function formatTime(seconds) {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function Trailer() {
  const sectionRef = useRef(null);
  const placeholderRef = useRef(null);
  const videoRef = useRef(null);

  const [mode, setMode] = useState('inline');
  const modeRef = useRef('inline');
  const setModeBoth = useCallback((m) => { modeRef.current = m; setMode(m); }, []);

  const [needsSoundTap, setNeedsSoundTap] = useState(false);
  const [ready, setReady] = useState(false);

  // Autoplay fires only ONCE (first time we cross into fullscreen). Subsequent
  // entries to fullscreen preserve whatever the video state currently is —
  // playing or paused. Scroll never overrides the user's play/pause choice
  // after the initial reveal.
  const hasAutoPlayedRef = useRef(false);

  // User-set mini position (drag). null = default bottom-right.
  const [miniPos, setMiniPos] = useState(null);

  // Player state surfaced from <video> events
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsIdleRef = useRef(null);

  // Motion values controlling container position/size
  const mvX = useMotionValue(0);
  const mvY = useMotionValue(0);
  const mvW = useMotionValue(0);
  const mvH = useMotionValue(0);

  // ─── Volume fade ────────────────────────────────────────────────
  const volAnimRef = useRef(null);
  const fadeVolume = useCallback((to, durationMs) => {
    const v = videoRef.current;
    if (!v) return;
    volAnimRef.current?.stop?.();
    const from = isFinite(v.volume) ? v.volume : 0;
    volAnimRef.current = animate(from, to, {
      duration: durationMs / 1000,
      ease: EASE,
      onUpdate: (val) => { if (v) v.volume = Math.max(0, Math.min(1, val)); },
    });
  }, []);

  // ─── Player actions ─────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.muted = false;
      v.play().then(() => {
        if (v.muted) setNeedsSoundTap(true);
      }).catch(() => {
        // Browser refused sound — fallback muted
        v.muted = true;
        v.play().catch(() => {});
        setNeedsSoundTap(true);
      });
    } else {
      v.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) {
      setNeedsSoundTap(false);
      fadeVolume(targetVolForMode(modeRef.current), 600);
    }
  }, [fadeVolume]);

  const handleSeek = useCallback((e) => {
    const v = videoRef.current;
    if (!v || !isFinite(v.duration)) return;
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    v.currentTime = pct * v.duration;
  }, []);

  // Auto-attempted playback (muted → unmute) for first autoplay at center
  const autoStartPlayback = useCallback(async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.muted = true;
      v.volume = 0;
      if (v.paused) await v.play();
      v.muted = false;
      if (v.muted) {
        setNeedsSoundTap(true);
      }
      // The 'play' event listener will pick up the fade-in; if already
      // playing, manually trigger it.
      if (!v.paused) fadeVolume(targetVolForMode(modeRef.current), 1800);
    } catch {
      /* ignored — muted play is virtually always allowed */
    }
  }, [fadeVolume]);

  const handleSoundTap = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setNeedsSoundTap(false);
    fadeVolume(targetVolForMode(modeRef.current), 1200);
  }, [fadeVolume]);

  // ─── Wire <video> events → React state ─────────────────────────
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;

    const onPlay = () => {
      setIsPlaying(true);
      fadeVolume(targetVolForMode(modeRef.current), 1500);
    };
    const onPause = () => {
      setIsPlaying(false);
      fadeVolume(0, 600);
    };
    const onTime = () => setCurrentTime(v.currentTime);
    const onMeta = () => setDuration(v.duration);
    const onVolume = () => setMuted(v.muted);

    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('durationchange', onMeta);
    v.addEventListener('volumechange', onVolume);

    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('durationchange', onMeta);
      v.removeEventListener('volumechange', onVolume);
    };
  }, [fadeVolume]);

  // ─── Inline placeholder tracking ───────────────────────────────
  // Sync the floating container to the placeholder's current viewport rect
  // on every scroll, resize, and layout mutation. Direct (no RAF batching)
  // since the operation is cheap and we need it to be reliable.
  useEffect(() => {
    if (mode !== 'inline') return undefined;
    const update = () => {
      const el = placeholderRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return;
      mvX.set(r.left);
      mvY.set(r.top);
      mvW.set(r.width);
      mvH.set(r.height);
      setReady(true);
    };

    update();
    // Force one more update after layout settles (CSS aspect-ratio / fonts).
    const t1 = window.setTimeout(update, 0);
    const t2 = window.setTimeout(update, 200);

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && placeholderRef.current) {
      ro = new ResizeObserver(update);
      ro.observe(placeholderRef.current);
    }
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [mode, mvX, mvY, mvW, mvH]);

  // ─── Animate to fullscreen / mini rects ────────────────────────
  useEffect(() => {
    if (mode === 'inline' || mode === 'closed') return undefined;
    const W = window.innerWidth;
    const H = window.innerHeight;
    const defaultMini = {
      x: W - MINI_W - MINI_PAD_RIGHT,
      y: H - MINI_H - MINI_PAD_BOTTOM,
    };
    // If the user has dragged the mini somewhere, restore that position
    // (clamped to viewport so a resize doesn't leave it off-screen).
    const userMini = miniPos
      ? {
          x: Math.max(8, Math.min(W - MINI_W - 8, miniPos.x)),
          y: Math.max(72, Math.min(H - MINI_H - 8, miniPos.y)),
        }
      : defaultMini;
    const target =
      mode === 'fullscreen'
        ? { x: 0, y: 0, w: W, h: H }
        : { x: userMini.x, y: userMini.y, w: MINI_W, h: MINI_H };
    const ctrls = [
      animate(mvX, target.x, { duration: 0.7, ease: EASE }),
      animate(mvY, target.y, { duration: 0.7, ease: EASE }),
      animate(mvW, target.w, { duration: 0.7, ease: EASE }),
      animate(mvH, target.h, { duration: 0.7, ease: EASE }),
    ];
    if (videoRef.current && !videoRef.current.paused && !videoRef.current.muted) {
      fadeVolume(targetVolForMode(mode), 600);
    }
    return () => ctrls.forEach((c) => c.stop?.());
  }, [mode, mvX, mvY, mvW, mvH, fadeVolume, miniPos]);

  // ─── Scroll-driven mode transitions ────────────────────────────
  useEffect(() => {
    const onScroll = () => {
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const sectionCenter = rect.top + rect.height / 2;
      const viewportCenter = window.innerHeight / 2;
      const delta = sectionCenter - viewportCenter; // + section below viewport center, − past
      const vh = window.innerHeight;

      // Asymmetric thresholds (per user request):
      //   "plus tard en descendant" — narrow entry zone going down, fullscreen
      //                                 triggers only when section is close to center.
      //   "plus tôt en remontant"   — quick exit going up, fullscreen releases
      //                                 to inline as soon as we move past center.
      //
      //                   ◀── UP (delta increasing) ─── DOWN (delta decreasing) ──▶
      //
      //   delta:  −∞ ──── −0.55vh ──── −0.45vh ──── 0 ──── +0.10vh ──── +0.15vh ── +∞
      //           MINI                FS                          FS              INLINE
      //                  ↑ MINI_BACK         ↑ FS_EXIT_UP      ↑ INLINE_BACK
      //                                          (UP early)       (DOWN late)
      //                       ↑ FS_EXIT_DOWN
      //                       (DOWN to mini)
      const INLINE_BACK   = vh * 0.15; // DOWN: inline → FS only near center (later)
      const FS_EXIT_DOWN  = vh * 0.55; // DOWN: FS → mini when well past
      const FS_EXIT_UP    = vh * 0.10; // UP:   FS → inline very early (sooner)
      const MINI_BACK     = vh * 0.45; // UP:   mini → FS when re-approaching

      const current = modeRef.current;

      if (current === 'closed') {
        // Closed by × button. User scrolled back UP above the section →
        // make the video reappear in inline form (paused, at original spot).
        if (delta > 0) setModeBoth('inline');
        return;
      }

      // Going DOWN ↓
      if (current === 'inline' && Math.abs(delta) < INLINE_BACK) {
        setModeBoth('fullscreen');
        // Auto-play only the FIRST time we ever enter fullscreen. Subsequent
        // entries respect whatever the user / video state currently is.
        if (!hasAutoPlayedRef.current) {
          hasAutoPlayedRef.current = true;
          autoStartPlayback();
        }
      } else if (current === 'fullscreen' && delta < -FS_EXIT_DOWN) {
        setModeBoth('mini');
      }
      // Going UP ↑
      else if (current === 'mini' && delta > -MINI_BACK) {
        setModeBoth('fullscreen');
      } else if (current === 'fullscreen' && delta > +FS_EXIT_UP) {
        setModeBoth('inline');
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [setModeBoth, autoStartPlayback]);

  // ─── Escape exits fullscreen ───────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && modeRef.current === 'fullscreen') {
        setModeBoth('mini');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setModeBoth]);

  // ─── Controls auto-hide in fullscreen ──────────────────────────
  useEffect(() => {
    if (mode !== 'fullscreen') {
      setControlsVisible(true);
      return undefined;
    }
    const show = () => {
      setControlsVisible(true);
      clearTimeout(controlsIdleRef.current);
      controlsIdleRef.current = setTimeout(() => {
        if (modeRef.current === 'fullscreen') setControlsVisible(false);
      }, 3000);
    };
    show();
    window.addEventListener('mousemove', show);
    window.addEventListener('touchstart', show);
    return () => {
      clearTimeout(controlsIdleRef.current);
      window.removeEventListener('mousemove', show);
      window.removeEventListener('touchstart', show);
    };
  }, [mode]);

  const handleClose = useCallback(() => {
    videoRef.current?.pause();
    // Fade already triggered by pause event; allow it to finish before
    // unmounting visuals.
    window.setTimeout(() => setModeBoth('closed'), 400);
  }, [setModeBoth]);

  const handleExpand = useCallback(() => {
    if (modeRef.current === 'mini') setModeBoth('fullscreen');
  }, [setModeBoth]);

  const zIndex = mode === 'fullscreen' ? 999 : mode === 'mini' ? 100 : 5;
  const showVideo = mode !== 'closed';
  const progress = duration > 0 ? currentTime / duration : 0;

  // Sizing helpers — controls bar dimensions scale with mode
  const isMini = mode === 'mini';
  const isFullscreen = mode === 'fullscreen';

  return (
    <>
      <section
        ref={sectionRef}
        id="trailer"
        style={{
          position: 'relative',
          minHeight: '110vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'clamp(40px, 6vw, 80px) clamp(20px, 5vw, 60px)',
          background: 'var(--bg)',
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 'clamp(80px, 8vw, 104px)',
            left: 'clamp(24px, 3vw, 48px)',
            fontFamily: 'var(--mono)',
            fontSize: 'clamp(9px, 0.7vw, 11px)',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.4)',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.6em',
          }}
        >
          <span>A·1 · LOST MY WAY</span>
          <span>CLIP · 2025</span>
        </div>

        <div
          ref={placeholderRef}
          aria-hidden="true"
          style={{
            width: 'min(70vw, 1000px)',
            aspectRatio: '16/9',
            visibility: 'hidden',
          }}
        />
      </section>

      {/*
        Portal the floating video into document.body so it escapes <main>'s
        stacking context (zIndex:1). Otherwise SiteNav (z:100) and Player
        (z:60), being siblings of <main>, would render ON TOP of the video
        and hide the controls bar, regardless of our local zIndex.
      */}
      {showVideo && typeof document !== 'undefined' && createPortal(
        <motion.div
          // Drag is enabled ONLY in mini mode. Position is captured on drag-end
          // and stored in miniPos so subsequent transitions restore it.
          drag={isMini}
          dragMomentum={false}
          dragElastic={0.08}
          dragConstraints={{
            left: 8,
            right: Math.max(8, window.innerWidth - MINI_W - 8),
            top: 72,
            bottom: Math.max(72, window.innerHeight - MINI_H - 8),
          }}
          onDragEnd={() => {
            // mvX / mvY have been mutated by Framer Motion during drag.
            setMiniPos({ x: mvX.get(), y: mvY.get() });
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            x: mvX,
            y: mvY,
            width: mvW,
            height: mvH,
            background: '#000',
            overflow: 'hidden',
            zIndex,
            border: isMini ? '1px solid rgba(255,255,255,0.18)' : 'none',
            boxShadow: isMini ? '0 20px 50px rgba(0,0,0,0.7)' : 'none',
            opacity: ready ? 1 : 0,
            transition: 'opacity 0.3s ease, border-color 0.4s, box-shadow 0.4s',
            pointerEvents: 'auto',
            cursor: isMini ? 'move' : undefined,
          }}
          onMouseMove={() => {
            if (isFullscreen) {
              setControlsVisible(true);
            }
          }}
        >
          <video
            ref={videoRef}
            src="/lost-my-way.mp4"
            poster="/lost-my-way-poster.jpg"
            playsInline
            muted
            preload="metadata"
            loop
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: isMini ? 'move' : 'none' }}
            aria-label="TSANGA — Lost My Way (clip)"
            onClick={(e) => {
              // In mini mode: click is reserved for drag start. Use the
              // dedicated play/pause + expand buttons instead.
              if (!isMini) {
                togglePlay();
              }
            }}
          />

          {/* ─── Controls overlay ───────────────────────────────── */}
          {!isMini && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                padding: isFullscreen ? 'clamp(16px, 2vw, 28px)' : '14px 18px',
                background:
                  'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 60%, transparent 100%)',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                opacity: controlsVisible ? 1 : 0,
                transition: 'opacity 0.35s ease',
                pointerEvents: controlsVisible ? 'auto' : 'none',
                zIndex: 2,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={togglePlay}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                style={controlButtonStyle}
              >
                {isPlaying ? '❚❚' : '▶'}
              </button>

              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 'clamp(10px, 0.85vw, 12px)',
                  letterSpacing: '0.18em',
                  color: 'rgba(255,255,255,0.85)',
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: 90,
                }}
              >
                {formatTime(currentTime)} <span style={{ opacity: 0.5 }}>/ {formatTime(duration)}</span>
              </span>

              {/* Progress bar */}
              <div
                role="slider"
                aria-label="Seek"
                aria-valuemin={0}
                aria-valuemax={duration || 0}
                aria-valuenow={currentTime}
                tabIndex={0}
                onClick={handleSeek}
                onKeyDown={(e) => {
                  const v = videoRef.current;
                  if (!v || !isFinite(v.duration)) return;
                  if (e.key === 'ArrowRight') v.currentTime = Math.min(v.duration, v.currentTime + 5);
                  if (e.key === 'ArrowLeft')  v.currentTime = Math.max(0, v.currentTime - 5);
                }}
                style={{
                  flex: 1,
                  height: 18,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'none',
                  outline: 'none',
                }}
              >
                <div style={{ position: 'relative', width: '100%', height: 2, background: 'rgba(255,255,255,0.2)' }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${progress * 100}%`,
                      background: 'rgba(255,255,255,0.85)',
                    }}
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={toggleMute}
                aria-label={muted ? 'Unmute' : 'Mute'}
                style={controlButtonStyle}
              >
                {muted ? '♪̸' : '♪'}
              </button>

              {isFullscreen && (
                <button
                  type="button"
                  onClick={() => setModeBoth('mini')}
                  aria-label="Exit fullscreen"
                  style={{ ...controlButtonStyle, fontSize: 13 }}
                >
                  ✕
                </button>
              )}
            </div>
          )}

          {/* ─── Fullscreen hint ───────────────────────────────── */}
          {isFullscreen && controlsVisible && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 'clamp(20px, 3vw, 40px)',
                left: '50%',
                transform: 'translateX(-50%)',
                fontFamily: 'var(--mono)',
                fontSize: 10,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.5)',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                opacity: controlsVisible ? 1 : 0,
                transition: 'opacity 0.35s',
              }}
            >
              ESC OR SCROLL TO EXIT
            </div>
          )}

          {/* ─── Mini controls : expand · play/pause · close + thin progress bottom ── */}
          {isMini && (
            <>
              {/* Centre play/pause */}
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'rgba(0,0,0,0.55)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  color: '#fff',
                  width: 38,
                  height: 38,
                  fontSize: 14,
                  fontFamily: 'var(--mono)',
                  cursor: 'pointer',
                  zIndex: 3,
                  display: 'grid',
                  placeItems: 'center',
                  lineHeight: 1,
                  borderRadius: 0,
                }}
              >
                {isPlaying ? '❚❚' : '▶'}
              </button>

              {/* Top-left : expand → fullscreen */}
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); handleExpand(); }}
                aria-label="Expand to fullscreen"
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 6,
                  background: 'rgba(0,0,0,0.55)',
                  border: 'none',
                  color: '#fff',
                  fontSize: 12,
                  width: 22,
                  height: 22,
                  cursor: 'pointer',
                  zIndex: 3,
                  display: 'grid',
                  placeItems: 'center',
                  lineHeight: 1,
                }}
              >
                ⤢
              </button>

              {/* Top-right : close */}
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
                aria-label="Close mini player"
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 6,
                  background: 'rgba(0,0,0,0.55)',
                  border: 'none',
                  color: '#fff',
                  fontSize: 14,
                  width: 22,
                  height: 22,
                  cursor: 'pointer',
                  zIndex: 3,
                  display: 'grid',
                  placeItems: 'center',
                  lineHeight: 1,
                }}
              >
                ×
              </button>

              {/* Thin progress at bottom */}
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: 0,
                  bottom: 0,
                  height: 2,
                  width: `${progress * 100}%`,
                  background: 'rgba(255,255,255,0.85)',
                  transition: 'width 0.1s linear',
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              />
            </>
          )}

          {/* ─── Sound-tap overlay (when unmute was blocked) ──── */}
          {needsSoundTap && isFullscreen && (
            <button
              type="button"
              onClick={handleSoundTap}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0,0,0,0.65)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: '#fff',
                fontFamily: 'var(--mono)',
                fontSize: 11,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                padding: '14px 24px',
                cursor: 'none',
                zIndex: 3,
              }}
            >
              ♪ Tap for sound
            </button>
          )}
        </motion.div>,
        document.body
      )}
    </>
  );
}

const controlButtonStyle = {
  background: 'none',
  border: 'none',
  color: 'rgba(255,255,255,0.9)',
  fontSize: 14,
  fontFamily: 'var(--mono)',
  padding: '6px 8px',
  cursor: 'none',
  display: 'grid',
  placeItems: 'center',
  width: 32,
  height: 32,
  lineHeight: 1,
};
